import React from 'react';
import { motion } from 'framer-motion';
import { BuildingIcon, CameraIcon, CheckIcon } from 'lucide-react';
import { BookingType } from '../../types/booking';
import { fees, properties } from '../../data/units';
import { formatNaira } from '../../utils/format';
const lowestRate = Math.min(...properties.map(p => p.nightlyRate));
const options = [{
  id: 'apartment' as BookingType,
  label: 'Apartment Stay',
  copy: 'Three- or two-bedroom apartment for overnight stays. Check-in 2 PM, checkout 12 noon.',
  price: `From ${formatNaira(lowestRate)}`,
  unit: 'per night',
  icon: BuildingIcon,
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/a1147893-9c81-497e-b832-78281b5e8175.jpg',
  alt: 'Living room of a Bel Air apartment'
}, {
  id: 'photoshoot' as BookingType,
  label: 'Photoshoot',
  copy: `A ${fees.photoshootHours}-hour session at the property for fashion, pre-wedding, brand or content shoots.`,
  price: formatNaira(fees.photoshootSession),
  unit: `per ${fees.photoshootHours}-hour session`,
  icon: CameraIcon,
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ee3f8718-77c7-4460-9496-88c17da9592b.jpg',
  alt: 'A photoshoot beside an arched window'
}];
export function BookingTypeSelector({
  value,
  onChange
}: {
  value: BookingType;
  onChange: (type: BookingType) => void;
}) {
  return <div role="radiogroup" aria-label="Booking type" className="grid gap-4 sm:grid-cols-2" data-id="element-91" data-mp="2yzatwsw1k78d">
      {options.map(option => {
      const selected = option.id === value;
      return <motion.button key={option.id} type="button" role="radio" aria-checked={selected} onClick={() => onChange(option.id)} whileTap={{
        scale: 0.98
      }} transition={{
        duration: 0.12
      }} className={`group flex flex-col overflow-hidden rounded-sm bg-ivory-50 text-left shadow-soft transition-shadow duration-200 ease-premium ${selected ? 'ring-2 ring-gold-400' : 'ring-1 ring-forest-700/10 hover:shadow-lift'}`} data-id="element-92" data-mp="37zpgrwonludz">
            <span className="relative block overflow-hidden" data-id="element-93" data-mp="2gxwyjf80gp2v">
              <img src={option.image} alt={option.alt} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-300 ease-premium group-hover:scale-[1.04]" data-id="element-94" data-mp="2uw4tjhlhlgbe" />
              <span className="absolute inset-0 bg-forest-800/25" aria-hidden="true" data-id="element-95" data-mp="34sg3i0xltu2o" />
              <span className={`absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150 ${selected ? 'bg-gold-400 text-forest-800' : 'bg-ivory-50/80 text-transparent'}`} aria-hidden="true" data-id="element-96" data-mp="2zv7lq1gdbs1y">
                <CheckIcon size={15} data-id="element-97" data-mp="3ilz5lwxzf0ul" />
              </span>
            </span>
            <span className="flex flex-1 flex-col p-6" data-id="element-98" data-mp="3ijuqsjyjohfj">
              <span className="flex items-center gap-2.5" data-id="element-99" data-mp="63ehgdrppbuj">
                <option.icon size={18} className="text-gold-500" aria-hidden="true" data-id="element-100" data-mp="b1fykt5kv13d" />
                <span className="font-display text-xl text-forest-700" data-id="element-101" data-mp="az3latarlewn">{option.label}</span>
              </span>
              <span className="mt-2.5 text-[13px] leading-relaxed text-muted" data-id="element-102" data-mp="139xuevic103">{option.copy}</span>
              <span className="mt-auto pt-5 font-display text-xl text-forest-700" data-id="element-103" data-mp="3jh9gncfk4kmj">
                {option.price}
                <span className="ml-1.5 font-sans text-[11px] text-gold-700" data-id="element-104" data-mp="3o7pxl6il6odn">{option.unit}</span>
              </span>
            </span>
          </motion.button>;
    })}
    </div>;
}